import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RequestTypesKeys } from '@app/enums/RequestTypes';
import { ExpenseTrackingService, RequestControllerv2Service } from '@app/generated-api';
import { PaymentRequestStatusChangeModalContentComponent } from '@app/v2/components/dialogs/request-status-change-modal/payment/payment-request-status-change-modal-content/payment-request-status-change-modal-content.component';
import { PaymentRequestStatusChangeFormGroup } from '@app/v2/components/dialogs/request-status-change-modal/payment/payment-request-status-change-modal-content/payment-request-status-change-modal-content.form';
import { RequestType } from '@app/v2/enums/request-types.enum';
import { PaymentRequestStatus, RequestStatus } from '@models/request';
import { StudyExpenseTrackingDataRow } from '@models/studyExpenseTrackingDataRow';
import { getModalConfig, ModalSize } from '@utility/modal-helper';
import { Observable } from 'rxjs';
import { BaseCellEditorComponent } from '../base-cell-editor/base-cell-editor.component';

@Component({
    selector: 'medpace-payment-request-status-change-modal-cell-editor',
    imports: [PaymentRequestStatusChangeModalContentComponent],
    standalone: true,
    templateUrl: './payment-request-status-change-modal-cell-editor.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentRequestStatusChangeModalCellEditorComponent<TContext = any> extends BaseCellEditorComponent<
    StudyExpenseTrackingDataRow,
    number,
    TContext,
    {
        requestData: {
            requestId: number;
            requestType: RequestTypesKeys;
            patientId: number;
        };
    }
> {
    generatedRequestService = inject(RequestControllerv2Service);
    generatedExpenseTrackingService = inject(ExpenseTrackingService);
    formGroup = new PaymentRequestStatusChangeFormGroup();
    requestType = computed(() => {
        const { requestType } = this.customParams().requestData;

        if (requestType === RequestTypesKeys.REIMBURSEMENT) return RequestType.Reimbursement;
        if (requestType === RequestTypesKeys.STIPEND) return RequestType.Stipend;
        if (requestType === RequestTypesKeys.TRAVEL) return RequestType.Travel;

        return undefined;
    });

    modalConfig = getModalConfig(ModalSize.Small);

    cancel() {
        this.baseParams().api.stopEditing(true);
    }
    confirm(formGroup: PaymentRequestStatusChangeFormGroup) {
        const customParams = this.customParams();
        const value = formGroup.value;

        const request: Observable<any> = (() => {
            if (value.newStatusId === PaymentRequestStatus.PendingERSubmission) {
                const requestBody = {
                    requestId: customParams.requestData.requestId,
                    patientId: customParams.requestData.patientId,
                    status: <RequestStatus>{ statusId: value.newStatusId },
                    additionalDetails: value.additionalInfo,
                    newStatus: PaymentRequestStatus[value.newStatusId].toString(),
                    requestType: customParams.requestData.requestType,
                    deducted: value.deductedFormGroup?.deducted,
                    deductedUsd: value.deductedFormGroup?.deductedUsd,
                    deductedCurrency: value.deductedFormGroup?.deductedCurrency?.isocode,
                };
                return customParams.requestData.requestType === RequestTypesKeys.REIMBURSEMENT
                    ? this.generatedExpenseTrackingService.expenseTrackingCompleteReimbursementRequest(requestBody)
                    : this.generatedExpenseTrackingService.expenseTrackingCompleteStipendRequest(requestBody);
            } else {
                return this.generatedRequestService.requestControllerv2ChangeRequestStatus({
                    requestId: customParams.requestData.requestId,
                    additionalDetails: value.additionalInfo,
                    newStatus: PaymentRequestStatus[value.newStatusId].toString(),
                    requestType: customParams.requestData.requestType,
                    deducted: value.deductedFormGroup?.deducted,
                    deductedUsd: value.deductedFormGroup?.deductedUsd,
                    deductedCurrency: value.deductedFormGroup?.deductedCurrency?.isocode,
                });
            }
        })();

        return request.subscribe({
            error: (e) => {
                this.baseParams().api.stopEditing(true);
            },
            complete: () => {
                this.baseParams().api.stopEditing(false);
                const comp = <any>this.baseParams().context; //circular dependecy, thats why any
                comp.loadData();
            },
        });
    }

    getValue(): number {
        return this.formGroup.controls.newStatusId.value;
    }
}
